import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/atoms/ui/dialog';
import { Input } from '@/components/atoms/ui/input';
import { Label } from '@/components/atoms/ui/label';
import { Button } from '@/components/atoms/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { createBoard, checkBoardNameExists } from '@/services/board.service';
import { addBoardToUser, canUserCreateBoard } from '@/services/user.service';
import { invalidateUserProfile } from '@/lib/cache';
import { validateBoardName, normalizeBoardName } from '@/lib/utils';
import { validateBoardDimensions, config } from '@/lib/config';
import {
  FiLock,
  FiGlobe,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { logButtonClick, logError } from '@/services/analytics.service';
import { COLOR_PALETTES } from '@/components/molecules/ColorPicker';
import { PaletteEditor } from '@/components/molecules/PaletteEditor';

interface CreateBoardDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
}

export const CreateBoardDialog = ({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  trigger,
}: CreateBoardDialogProps = {}) => {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;
  const [name, setName] = useState('');
  const [width, setWidth] = useState(config.defaultBoardWidth.toString());
  const [height, setHeight] = useState(config.defaultBoardHeight.toString());
  const [isPublic, setIsPublic] = useState(true);
  const [customPalette, setCustomPalette] = useState<string[]>(
    COLOR_PALETTES[Math.floor(Math.random() * COLOR_PALETTES.length)].colors.map((c) => c)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState<'width' | 'height' | null>(null);
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    startWidth: 0,
    startHeight: 0,
  });

  const totalPixels = parseInt(width) * parseInt(height);
  const isValidDimensions = validateBoardDimensions(
    parseInt(width),
    parseInt(height)
  );

  // Reset to step 1 when dialog opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setCurrentStep(1);
      setError(null);
      const randomIndex = Math.floor(Math.random() * COLOR_PALETTES.length);
      setCustomPalette(COLOR_PALETTES[randomIndex].colors.map((c) => c));
    }
  };


  // Get dynamic dialog title based on step
  const getDialogTitle = (): string => {
    if (currentStep === 1) return 'Create New Board';
    if (currentStep === 2) return 'Set Board Dimensions';
    if (currentStep === 3) return 'Choose Color Palette';
    return 'Create New Board';
  };

  // Step validation
  const canProceedToNextStep = () => {
    if (currentStep === 1) {
      return name.trim().length > 0;
    }
    if (currentStep === 2) {
      const w = parseInt(width);
      const h = parseInt(height);
      return !isNaN(w) && !isNaN(h) && w > 0 && h > 0 && isValidDimensions;
    }
    if (currentStep === 3) {
      // Step 3: All validations must pass
      const nameValidation = validateBoardName(name);
      const w = parseInt(width);
      const h = parseInt(height);
      const paletteValid =
        customPalette.length === 8 &&
        customPalette.every((c) => /^#[0-9A-F]{6}$/i.test(c));
      return (
        nameValidation.valid &&
        !isNaN(w) &&
        !isNaN(h) &&
        w > 0 &&
        h > 0 &&
        isValidDimensions &&
        paletteValid
      );
    }
    return true;
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (currentStep < 3 && canProceedToNextStep()) {
      setCurrentStep(currentStep + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };


  // Resize handlers
  const handleResizeStart = (type: 'width' | 'height', e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!previewContainerRef.current) return;

    const currentWidth = parseInt(width) || 1;
    const currentHeight = parseInt(height) || 1;

    setIsResizing(type);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      startWidth: currentWidth,
      startHeight: currentHeight,
    });
  };

  useEffect(() => {
    if (!isResizing || !previewContainerRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!previewContainerRef.current) return;

      const rect = previewContainerRef.current.getBoundingClientRect();
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;

      if (isResizing === 'width') {
        // Calculate scale: board pixels per screen pixel
        const pixelsPerScreenPixel = resizeStart.startWidth / rect.width;
        const deltaPixels = deltaX * pixelsPerScreenPixel;
        const newWidth = Math.max(
          1,
          Math.round(resizeStart.startWidth + deltaPixels)
        );
        setWidth(newWidth.toString());
      } else if (isResizing === 'height') {
        // Calculate scale: board pixels per screen pixel
        const pixelsPerScreenPixel = resizeStart.startHeight / rect.height;
        const deltaPixels = deltaY * pixelsPerScreenPixel;
        const newHeight = Math.max(
          1,
          Math.round(resizeStart.startHeight + deltaPixels)
        );
        setHeight(newHeight.toString());
      }
    };

    const handleMouseUp = () => {
      setIsResizing(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;
    if (currentStep !== 3) return;

    logButtonClick('Create Board Submit', 'CreateBoardDialog');

    const nameValidation = validateBoardName(name);
    if (!nameValidation.valid) {
      setError(nameValidation.error || 'Invalid board name');
      return;
    }

    const w = parseInt(width);
    const h = parseInt(height);

    if (isNaN(w) || isNaN(h) || w < 1 || h < 1) {
      setError('Width and height must be positive numbers');
      return;
    }

    if (!validateBoardDimensions(w, h)) {
      setError(
        `Total pixels (${w * h}) exceeds maximum allowed (${
          config.maxBoardPixels
        })`
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const canCreate = await canUserCreateBoard(user.uid);
      if (!canCreate) {
        setError(`You can only create up to ${config.maxBoardsPerUser} boards`);
        setLoading(false);
        return;
      }

      // Check if a board with this name already exists
      const nameExists = await checkBoardNameExists(name.trim(), user.uid);
      if (nameExists) {
        setError(
          `A board with the name "${name.trim()}" already exists. Please choose a different name.`
        );
        setLoading(false);
        return;
      }

      const board = await createBoard(
        user.uid,
        user.username,
        name,
        w,
        h,
        isPublic,
        false,
        undefined,
        customPalette
      );
      await addBoardToUser(user.uid, board);

      // Invalidate user profile cache to refresh boards list
      invalidateUserProfile(user.uid);

      // Refresh user data in AuthContext
      await refreshUser();

      toast({
        title: 'Board created!',
        description: `Your board "${name}" has been created successfully.`,
      });

      setOpen(false);
      setName('');
      setWidth(config.defaultBoardWidth.toString());
      setHeight(config.defaultBoardHeight.toString());
      setIsPublic(true);
      const randomIndex = Math.floor(Math.random() * COLOR_PALETTES.length);
      setCustomPalette(COLOR_PALETTES[randomIndex].colors.map((c) => c));
      setCurrentStep(1);

      navigate(`/board/${board.name}`);
    } catch (error) {
      console.error('Board creation error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create board';
      logError('Board Creation Failed', errorMessage, 'CreateBoardDialog', {
        board_name: name,
        width: w,
        height: h,
        is_public: isPublic,
      });
      setError(errorMessage);
      toast({
        title: 'Creation failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="create-board-dialog-component sm:max-w-[500px]">
        <DialogHeader className="relative">
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>Step {currentStep} of 3</DialogDescription>
          {/* Step Indicator */}
          <div className="absolute top-0 right-0 flex items-center gap-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`h-2 rounded-full transition-all ${
                  step === currentStep
                    ? 'w-8 bg-primary'
                    : step < currentStep
                      ? 'w-2 bg-primary/50'
                      : 'w-2 bg-muted'
                }`}
              />
            ))}
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Step 1: Name & Visibility */}
            {currentStep === 1 && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="name">Board Name</Label>
                  <Input
                    id="name"
                    placeholder="My Awesome Board"
                    value={name}
                    onChange={(e) => {
                      setName(normalizeBoardName(e.target.value));
                      setError(null);
                    }}
                    disabled={loading}
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Privacy</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={isPublic ? 'default' : 'outline'}
                      className="flex-1"
                      onClick={() => {
                        logButtonClick('Set Board Public', 'CreateBoardDialog');
                        setIsPublic(true);
                      }}
                      disabled={loading}
                    >
                      <FiGlobe className="mr-2 h-4 w-4" />
                      Public
                    </Button>
                    <Button
                      type="button"
                      variant={!isPublic ? 'default' : 'outline'}
                      className="flex-1"
                      onClick={() => {
                        logButtonClick(
                          'Set Board Private',
                          'CreateBoardDialog'
                        );
                        setIsPublic(false);
                      }}
                      disabled={loading}
                    >
                      <FiLock className="mr-2 h-4 w-4" />
                      Private
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {isPublic
                      ? 'Anyone can view and contribute to this board'
                      : 'Only you can view and edit this board'}
                  </p>
                </div>
              </>
            )}

            {/* Step 2: Dimensions with Preview */}
            {currentStep === 2 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="width">Width</Label>
                    <Input
                      id="width"
                      type="number"
                      min="1"
                      max="1295"
                      value={width}
                      onChange={(e) => {
                        setWidth(e.target.value);
                        setError(null);
                      }}
                      disabled={loading}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="height">Height</Label>
                    <Input
                      id="height"
                      type="number"
                      min="1"
                      max="1295"
                      value={height}
                      onChange={(e) => {
                        setHeight(e.target.value);
                        setError(null);
                      }}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Dimension Preview */}
                <div className="flex items-center justify-center py-8 px-16">
                  <div
                    ref={previewContainerRef}
                    className="relative w-full max-w-full flex items-center justify-center"
                    style={{
                      aspectRatio: `${parseInt(width) || 1} / ${parseInt(height) || 1}`,
                    }}
                  >
                    {/* Dimension Labels */}
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-sm font-mono text-muted-foreground whitespace-nowrap">
                      W: {width || '0'}
                    </div>
                    <div className="absolute -left-16 top-1/2 -translate-y-1/2 text-sm font-mono text-muted-foreground whitespace-nowrap -rotate-90">
                      H: {height || '0'}
                    </div>
                    {/* Preview Rectangle with Pixel Grid */}
                    <div
                      className="border-2 border-primary bg-primary/10 w-full h-full relative overflow-hidden"
                      style={{
                        minWidth: '60px',
                        minHeight: '60px',
                      }}
                    >
                      {/* Pixel Grid - Every 8 pixels */}
                      <div
                        className="absolute"
                        style={{
                          top: '-1px',
                          left: '-1px',
                          right: '0',
                          bottom: '0',
                          backgroundImage: `
                            linear-gradient(to right, hsl(var(--foreground) / 0.1) 1px, transparent 1px),
                            linear-gradient(to bottom, hsl(var(--foreground) / 0.1) 1px, transparent 1px)
                          `,
                          backgroundSize: `${(100 * 8) / (parseInt(width) || 1)}% ${(100 * 8) / (parseInt(height) || 1)}%`,
                        }}
                      />
                      {/* Resize Handle - Right edge (width) */}
                      <div
                        className="absolute top-0 right-0 w-2 h-full cursor-ew-resize hover:bg-primary/20 transition-colors"
                        onMouseDown={(e) => handleResizeStart('width', e)}
                        style={{ zIndex: 10 }}
                      />
                      {/* Resize Handle - Bottom edge (height) */}
                      <div
                        className="absolute bottom-0 left-0 w-full h-2 cursor-ns-resize hover:bg-primary/20 transition-colors"
                        onMouseDown={(e) => handleResizeStart('height', e)}
                        style={{ zIndex: 10 }}
                      />
                    </div>
                  </div>
                </div>

                {!isValidDimensions && (
                  <div className="rounded-lg bg-muted p-3">
                    <p className="text-sm">
                      Total pixels:{' '}
                      <span className="font-mono">
                        {totalPixels.toLocaleString()}
                      </span>
                    </p>
                    <p className="text-sm">
                      Maximum allowed:{' '}
                      <span className="font-mono">
                        {config.maxBoardPixels.toLocaleString()}
                      </span>
                    </p>
                    <p className="text-sm text-destructive mt-1">
                      Dimensions exceed maximum!
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Step 3: Palette Selection */}
            {currentStep === 3 && (
              <>
                <div className="grid gap-4">
                  <PaletteEditor
                    palette={customPalette}
                    onPaletteChange={setCustomPalette}
                    disabled={loading}
                  />
                  <div className="rounded-lg bg-muted/50 p-3 border border-primary/20">
                    <p className="text-sm text-muted-foreground">
                      💡 Don't worry! You can change the palette later from the
                      board settings.
                    </p>
                  </div>
                </div>
              </>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter className="justify-between">
            {currentStep === 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  logButtonClick('Cancel Create Board', 'CreateBoardDialog');
                  handleOpenChange(false);
                }}
                disabled={loading}
              >
                Cancel
              </Button>
            )}
            <div className="flex gap-2">
              {currentStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Back
                </Button>
              )}
              {currentStep < 3 ? (
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNext(e);
                  }}
                  disabled={loading || !canProceedToNextStep()}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={loading || !canProceedToNextStep()}
                >
                  {loading ? 'Creating...' : 'Create Board'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
